/**
 * Local sample store backed by IndexedDB. Lets users import audio
 * files into Busswerks without round-tripping through S3 — useful
 * on native apps (Mac Catalyst, iOS) and for users who want to
 * audition a sample before deciding whether it deserves cloud
 * storage.
 *
 * Local samples are scoped to the device's IndexedDB and don't
 * sync. Opening the same project on a different device that
 * references a local sample by id renders the slice as missing
 * (silent fallback in the audio engine, same path as a cloud
 * sample whose s3Key got deleted).
 *
 * Schema:
 *   database: busswerks-local-samples (v1)
 *   object store: samples (keyPath: 'id')
 *
 * The Blob is stored verbatim. Read-side generates a `blob:` URL
 * via URL.createObjectURL on demand and puts it in `Sample.playUrl`,
 * so the audio engine's existing fetch path works without
 * modification — fetch+arrayBuffer+decodeAudioData all handle
 * blob: URLs identically to https: URLs.
 */

import type { Sample, SampleSlice } from '@/hooks/useSampleUpload'

const DB_NAME = 'busswerks-local-samples'
const DB_VERSION = 1
const STORE = 'samples'

export interface LocalSampleRow {
  id: string
  name: string
  contentType: string
  sizeBytes: number
  durationSec?: number
  bpm?: number
  bars?: number
  bpmConfidence?: number
  slices?: SampleSlice[]
  /** Non-destructive sample-start point in seconds (slicer-only). */
  trimStartSec?: number
  /** Non-destructive sample-end point in seconds (slicer-only). */
  trimEndSec?: number
  /** Per-sample gain in dBFS. Multiplied with velocity + slice gain
   *  at playback. `undefined` = 0 dB. */
  gainDb?: number
  /** Peak value (0..1) from the last normalize run. Surfaced in the
   *  Slicer UI so users can tell whether gainDb came from normalize
   *  or was set manually. */
  normalizedPeak?: number
  /** Audio bytes. Stored as a Blob so structured-clone is cheap and
   *  we don't pay for ArrayBuffer↔Blob conversions on read/write. */
  bytes: Blob
  createdAt: string
  updatedAt: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  if (typeof indexedDB === 'undefined') {
    console.error('[local-db] IndexedDB not available in this environment')
    return Promise.reject(new Error('IndexedDB not available'))
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      console.info('[local-db] upgrade running', {
        oldVersion: req.transaction?.db.version,
        newVersion: DB_VERSION,
      })
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => {
      console.info('[local-db] open ok')
      resolve(req.result)
    }
    req.onerror = () => {
      console.error('[local-db] open failed', req.error)
      reject(req.error ?? new Error('IndexedDB open failed'))
    }
    req.onblocked = () => {
      console.error(
        '[local-db] open blocked — another tab holds an older version',
      )
    }
  })
  return dbPromise
}

function awaitRequest<T>(req: IDBRequest<T>, opName?: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      console.error(`[local-db] ${opName ?? 'request'} failed`, req.error)
      reject(req.error ?? new Error('IndexedDB request failed'))
    }
  })
}

export async function listLocalSampleRows(): Promise<LocalSampleRow[]> {
  const db = await openDb()
  const store = db.transaction(STORE, 'readonly').objectStore(STORE)
  const all = await awaitRequest(
    store.getAll() as IDBRequest<LocalSampleRow[]>,
    'getAll',
  )
  // Sort newest first so the dropdown picker matches the cloud
  // list's ordering convention (also newest-first there).
  return all.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))
}

export async function getLocalSampleRow(
  id: string,
): Promise<LocalSampleRow | undefined> {
  const db = await openDb()
  const store = db.transaction(STORE, 'readonly').objectStore(STORE)
  return awaitRequest(
    store.get(id) as IDBRequest<LocalSampleRow | undefined>,
    `get(${id})`,
  )
}

export async function putLocalSampleRow(row: LocalSampleRow): Promise<void> {
  // Defensive revoke: any path that overwrites a row MUST invalidate
  // the cached blob URL, otherwise readers get a stale URL pointing
  // at the previous Blob (which is no longer reachable from anywhere
  // and gets GC'd, leaving a dangling blob: URL). This is the safety
  // net for any future mutation that forgets to revoke explicitly.
  revokeBlobUrl(row.id)
  const db = await openDb()
  const store = db.transaction(STORE, 'readwrite').objectStore(STORE)
  await awaitRequest(store.put(row), `put(${row.id})`)
}

export async function deleteLocalSampleRow(id: string): Promise<void> {
  const db = await openDb()
  const store = db.transaction(STORE, 'readwrite').objectStore(STORE)
  await awaitRequest(store.delete(id), `delete(${id})`)
}

/**
 * Per-id blob URL cache. Without this, every refetch of the
 * samples list would mint a new blob URL and the old one would
 * become a leaked Blob reference (browsers don't auto-revoke
 * blob URLs — they live until URL.revokeObjectURL is called or
 * the document unloads). The cache maps id → currently-issued
 * URL; when a row is deleted or replaced, we revoke the old URL
 * before issuing a new one.
 */
const blobUrlCache = new Map<string, string>()

function urlForRow(row: LocalSampleRow): string {
  const cached = blobUrlCache.get(row.id)
  if (cached) return cached
  const url = URL.createObjectURL(row.bytes)
  blobUrlCache.set(row.id, url)
  return url
}

export function revokeBlobUrl(id: string): void {
  const url = blobUrlCache.get(id)
  if (url) {
    URL.revokeObjectURL(url)
    blobUrlCache.delete(id)
  }
}

/**
 * Convert a stored row into the Sample shape the rest of the app
 * already understands. Sets `source: 'local'` so consumers that
 * need to branch on origin (Slicer save, sample-miss event) can
 * tell. `s3Key` is empty since the sample lives only on this
 * device; `playUrl` is a blob: URL.
 */
export function rowToSample(row: LocalSampleRow): Sample {
  return {
    id: row.id,
    name: row.name,
    contentType: row.contentType,
    sizeBytes: row.sizeBytes,
    s3Key: '',
    durationSec: row.durationSec,
    bpm: row.bpm,
    bars: row.bars,
    bpmConfidence: row.bpmConfidence,
    slices: row.slices,
    trimStartSec: row.trimStartSec,
    trimEndSec: row.trimEndSec,
    gainDb: row.gainDb,
    normalizedPeak: row.normalizedPeak,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    playUrl: urlForRow(row),
    source: 'local',
  }
}
